import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  BundlePriceInitialValues,
  BundlePriceInterface,
} from '../../models/dashboard/revenue/bundle-price';
import { LookupPriceInterface } from '../../models/dashboard/revenue/lookup-price';

type PriceManagementStateItem = BundlePriceInterface & { id: string; };

const createPricingBundleStateItem = (pricingBundle: BundlePriceInterface): PriceManagementStateItem => ({
  ...pricingBundle,
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
});

const getInitialState = () => ({
  pricingBundleSize: 1,
  payAsYouGoPrice: '',
  pricingBundle: [createPricingBundleStateItem(BundlePriceInitialValues)],
});

interface IInitialState {
  pricingBundleSize: number;
  payAsYouGoPrice: string;
  pricingBundle: PriceManagementStateItem[];
}

interface UpdatePricingBundlePayload {
  id: string;
  pricingBundle: BundlePriceInterface;
}


const initialState: IInitialState = getInitialState();

const createPriceManagementSlice = createSlice({
  name: 'createPriceManagement',
  initialState,
  reducers: {
    setPricingBundles(state, action: PayloadAction<BundlePriceInterface>) {
      state.pricingBundle = [...state.pricingBundle, createPricingBundleStateItem(action.payload)];
      state.pricingBundleSize = state.pricingBundle.length;
    },

    addEmptyPricingBundle(state) {
      state.pricingBundle = [...state.pricingBundle, createPricingBundleStateItem(BundlePriceInitialValues)];
      state.pricingBundleSize = state.pricingBundle.length;
    },

    updatePricingBundlesAtId(state, action: PayloadAction<UpdatePricingBundlePayload>) {
      state.pricingBundle = state.pricingBundle.map((pricingBundle) => {
        if (pricingBundle.id === action.payload.id) {
          return {
            ...pricingBundle,
            ...action.payload.pricingBundle,
          };
        }

        return pricingBundle;
      });
    },

    initializePricingBundlesFromApi(state, action: PayloadAction<BundlePriceInterface[]>) {
      state.pricingBundle = action.payload.map(createPricingBundleStateItem);
      state.pricingBundleSize = state.pricingBundle.length;
    },

    deletePricingBundlesAtId(state, action: PayloadAction<string>) {
      state.pricingBundle = state.pricingBundle.filter((pricingBundle) => pricingBundle.id !== action.payload);
      state.pricingBundleSize = Math.max(1, state.pricingBundle.length);
    },

    setPayAsYouGoPrice(state, action: PayloadAction<LookupPriceInterface>) {
      state.payAsYouGoPrice = action.payload.price;
    },

    reset: () => getInitialState(),
  },
});

export const createPriceManagementActions = createPriceManagementSlice.actions;
export default createPriceManagementSlice.reducer;
